import {
  SERVICE_BASIC_CARE,
  SERVICE_BASIC_MEDICAL_CARE,
  SERVICE_CARE_PROTECTED_AREA,
  SERVICE_CLOSED_AREA,
  SERVICE_COMPANIONSHIP,
  SERVICE_DEMENTIA,
  SERVICE_PALLIATIVE_CARE,
  SERVICE_PALLIATIVE_CERTIFICATE,
  SERVICE_PSYCHIATRIC_CARE,
  SERVICE_SPECIALISED_INTENSIVE_NURSE,
  SERVICE_TRACHEOSTOMY,
  SOLUTION_SHORT_TERM_STATIC_CARE,
} from "core/consts";
import { isCareSearch, isRehabSearch } from "core/model/auctions";
import { getAge } from "core/model/patients";
import { formatUnixDate } from "core/model/utils/dates";
import { formatDistance } from "core/model/utils/location";
import { formatMedicalSuppliesLocation } from "core/model/utils/location/formatters";
import { capitaliseFirst } from "core/model/utils/strings";
import {
  Auction,
  AuctionRequest,
  GetOntologyType,
  Solutions,
} from "core/types";
import { isValid } from "date-fns";
import { getOrderedItems } from "ds_legacy/components/ChipGroup";
import { solutionSortOrder } from "dsl/ecosystems/PatientCreatePage/PatientCreateForm";
import {
  ArrayChip,
  BooleanChip,
  NumberChip,
  OntologyChip,
  StringChip,
} from "dsl/organisms/PatientProfileDetailsShared/shared";
import { Locale as LocaleString } from "translations";
import Translations from "translations/types";

const shouldDisplayDuration = (duration: number, solutions: Solutions) =>
  solutions.includes(SOLUTION_SHORT_TERM_STATIC_CARE) ? duration <= 60 : true;

const getBirthDateRange = (birthDate: string, ageRange: string) =>
  `${birthDate}${
    ageRange && typeof ageRange === "string" ? ` (${ageRange})` : ""
  }`;

function computeCarestartFlexibility(
  auction: Auction,
  locale: LocaleString,
  translations: Translations,
): string | null {
  const careStartDate = auction.start_date;
  const flexible = auction.start_date_flexible
    ? ` ${translations.patient.flexible}`
    : "";

  if (!careStartDate) return null;
  return formatUnixDate(careStartDate, locale).concat(flexible);
}

function formatDistanceForZipCodeMessage(
  distance: number | null | undefined,
  translations: Translations,
): string {
  if (!distance || distance <= 0) {
    return "";
  }
  return distance > 0
    ? translations.patient.distanceFromYou({
        distance: formatDistance(distance),
      })
    : "";
}

export function StaticPatientProfileTransferChips({
  auction,
  auctionRequest,
  getOntology,
  isDisabled = false,
  isForClinic,
  locale,
  showServices,
  showSolutions,
  showSpecializations,
  translations,
}: {
  isDisabled?: boolean;
  isForClinic?: boolean;
  getOntology: GetOntologyType;
  locale: LocaleString;
  translations: Translations;
  auction: Auction;
  auctionRequest: AuctionRequest;
  showServices?: boolean;
  showSolutions?: boolean;
  showSpecializations?: boolean;
}) {
  const solutionsToUse = showSolutions
    ? auctionRequest?.solutions ?? auction.solutions ?? []
    : [];

  let zipcodeString = formatMedicalSuppliesLocation(auction);

  const distanceForZipCode = formatDistanceForZipCodeMessage(
    auctionRequest?.distance,
    translations,
  );

  zipcodeString = zipcodeString
    ? `${zipcodeString}${distanceForZipCode ? ` (${distanceForZipCode})` : ""}`
    : undefined;

  const services = showServices ? auction.services : [];

  const specializations = showSpecializations ? auction.specializations : [];

  const age =
    getAge(auction.patient.profile?.age_interval, auction.search_type) ?? "";

  return (
    <>
      <OntologyChip
        getOntology={getOntology}
        isBold
        isDisabled={isDisabled}
        isSelected
        keyPrefix="patientType"
        type="patientType"
        value={isForClinic && auction.search_type}
      />

      <ArrayChip
        getOntology={getOntology}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="solution"
        type="solution"
        value={
          solutionsToUse.length > 0
            ? getOrderedItems(
                solutionSortOrder,
                solutionsToUse.map((value) => ({ value })),
              )?.map((item) => item.value) || null
            : null
        }
      />
      <ArrayChip
        getOntology={getOntology}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="specializations"
        type="specializations"
        value={specializations}
      />
      {auction.profile?.is_elective && (
        <StringChip
          hasTextBreak
          isBold
          isSelected
          keyPrefix="elective"
          value={translations.careproviderApp.showElectivePatient}
        />
      )}
      <StringChip
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="start_date"
        prefix={translations.patient.expectedStartDate}
        value={computeCarestartFlexibility(auction, locale, translations)}
      />
      {isRehabSearch(auction?.search_type) &&
        auction?.patient?.profile?.discharge_date &&
        isValid(auction.patient.profile.discharge_date) && (
          <StringChip
            isBold
            isDisabled={isDisabled}
            isPrimary
            keyPrefix="discharge_date"
            prefix={translations.patient.expectedDischargeDate}
            value={formatUnixDate(
              auction.patient.profile.discharge_date,
              locale,
            )}
          />
        )}
      <BooleanChip
        hasValue={
          auction.patient.profile?.direct_transfer_necessary != null
        }
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="direct_transfer_necessary"
        label={translations.patient.directTransfer}
      />
      {isCareSearch(auction?.search_type) && (
        <>
          <NumberChip
            isBold
            isDisabled={isDisabled}
            isPrimary
            keyPrefix="care_duration_in_days"
            prefix={translations.patient.careDuration}
            transform={(value) =>
              getOntology({ key: value, type: "careDurations" })
            }
            value={
              ((isForClinic ||
                shouldDisplayDuration(
                  auction.patient.profile?.care_duration_in_days ?? 0,
                  solutionsToUse,
                )) &&
                auction.patient.profile?.care_duration_in_days) ||
              null
            }
          />
          <BooleanChip
            hasValue={!!auction.patient.profile?.interested_long_term_stay}
            isBold
            isDisabled={isDisabled}
            isPrimary
            keyPrefix="long_term_stay"
            label={capitaliseFirst(
              translations.patient.interestedStayLongTermRequest,
            )}
          />
        </>
      )}
      {auction?.patient?.profile?.birth_date?.decrypted ? (
        <StringChip
          isBold
          isDisabled={isDisabled}
          isPrimary
          keyPrefix="birth_date"
          prefix={translations.patient.dateOfBirth}
          value={getBirthDateRange(
            auction.patient?.profile?.birth_date?.decrypted ?? "",
            age,
          )}
        />
      ) : (
        <StringChip
          isBold
          isDisabled={isDisabled}
          isPrimary
          keyPrefix="age"
          prefix={translations.patient.age}
          value={age}
        />
      )}
      <StringChip
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="zipcode"
        prefix={translations.address.zipcode}
        value={zipcodeString}
      />
      <StringChip
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="radius_in_km"
        prefix={translations.patient.searchRadius}
        suffix="km"
        value={
          isForClinic && auction.radius_in_meter
            ? (auction.radius_in_meter / 1000).toString()
            : null
        }
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_CLOSED_AREA)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="closed_area"
        label={translations.patient.closedAreaNeeded}
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_CARE_PROTECTED_AREA)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="closed_area"
        label={translations.patient.mentalState.protectedAreaNeeded.label}
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_PALLIATIVE_CARE)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="palliative_care"
        label={translations.patient.palliativeCare.label}
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_PALLIATIVE_CERTIFICATE)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="palliative_certificate"
        label={
          translations.patient.medicalDiagnosis.palliativeCertificateNeeded
        }
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_TRACHEOSTOMY)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="tracheostomy"
        label={translations.patient.careNeeds.tracheostomy.label}
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_SPECIALISED_INTENSIVE_NURSE)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="specialised_intensive_nurse"
        label={translations.ontologies.service.values.specialisedIntensiveNurse}
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_PSYCHIATRIC_CARE)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="psychiatric_care"
        label={translations.patient.careNeeds.psychiatric_care.label}
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_BASIC_MEDICAL_CARE)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="basic_medical_care"
        label={translations.ontologies.service.values.basicMedicalCare}
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_BASIC_CARE)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="basic_care"
        label={translations.patient.careNeeds.baseCare}
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_COMPANIONSHIP)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="companionship"
        label={translations.patient.careNeeds.companionship}
      />
      <BooleanChip
        hasValue={!!services?.includes(SERVICE_DEMENTIA)}
        isBold
        isDisabled={isDisabled}
        isPrimary
        keyPrefix="dementia"
        label={translations.patient.dementia}
      />
    </>
  );
}
